import React, { useMemo, useState } from 'react';
import { ErrorBoundaryConstellation } from '../../../../constellationExtended/ErrorBoundary/ErrorBoundaryConstellation';
import { IOrderParams } from '../../../../API/pcDashboard/getOrderAsync';
import { translateOrderParamsToTaskParams } from '../../util/translateOrderParamsToTaskParams';
import { Card } from '@zillow/constellation';
import { TableCardHeader } from '../components/TableCardHeader';
import { useListPagedTasksByFilterParamsQuery } from '../../../../API/pcDashboard/getTasksAsync';
import { createDefaultPageData } from '../../../../types/rest/IPageData';
import { IPCDashboardTaskData } from '../../../../types/pcDashboard/IDashboardTasks';
import { TasksTabNames, TaskTabList } from './TaskTabList';
import { TasksTable } from './TasksTable';
import { useDetermineStartDateEndDateParams } from '../hooks/useDetermineStartDateEndDateParams';
import { useManageTaskPagination } from './hooks/useManageTaskPagination';

const TASKS_ENTITY = 'tasks';

export const DashboardTasksContainer: React.FC<{
  queryWithoutPagination: Omit<IOrderParams, 'pageNumber' | 'pageSize' | 'sortBy'>;
}> = ({ queryWithoutPagination }) => {
  const taskParams = useMemo(() => {
    return translateOrderParamsToTaskParams(queryWithoutPagination);
  }, [queryWithoutPagination]);
  const [selectedTab, setSelectedTab] = useState(TasksTabNames.AllTasks);

  const {
    paginationState: [paginationSortParams, setPaginationSortParams],
    revertToFirstPage,
  } = useManageTaskPagination(queryWithoutPagination);

  const determineStartDateEndDateParams = useDetermineStartDateEndDateParams();

  const startEndParams = determineStartDateEndDateParams(selectedTab, TASKS_ENTITY);

  const taskFilterParams = useMemo(
    () => ({ ...taskParams, ...startEndParams, ...paginationSortParams }),
    [taskParams, startEndParams, paginationSortParams]
  );

  const {
    data = createDefaultPageData<IPCDashboardTaskData>(),
    isFetching,
    isSuccess,
    refetch,
  } = useListPagedTasksByFilterParamsQuery(taskFilterParams);

  return (
    <ErrorBoundaryConstellation>
      <Card cardType={'secondary'} cardElevation={0}>
        <TableCardHeader refetch={refetch} title={'Tasks'} />
        <TaskTabList
          selectedTab={selectedTab}
          setSelectedTab={setSelectedTab}
          taskParams={taskParams}
          revertToFirstPage={revertToFirstPage}
        />
        <TasksTable
          data={data}
          isFetching={isFetching}
          isSuccess={isSuccess}
          setTaskPaginationSortParams={setPaginationSortParams}
        />
      </Card>
    </ErrorBoundaryConstellation>
  );
};
